import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import { useErrorInputs } from '../../../hooks/useErrorInputs';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useTranslation } from "react-i18next";
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import moment from 'moment';
import { fetchServiceJson, fecthResolutions } from '../../../services/common.services';
import { MultipleSelect } from '../../../components/others/MultipleSelect';
export const InvoiceFom = forwardRef(({ dto, supplierId, handleInputChange, handleDirectInputChange, setRefreshTotals }, ref) => {
    const [errorInputs, , , validForm] = useErrorInputs({});
    const [{ userSession }] = useDataAuth();
    const { t } = useTranslation();
    const fieldsRequired = [
        'expedition_date',
        'currency',
        'id_payment_method',
        'resolution',
        'payment_due_date',
        'id_mean_of_payment',
        ...(dto.retentions.includes('01') ? ['ica_retention_rate'] : [])
    ];

    const paymentMethods = [
        {code: "1", name: t('commonFields.typeCredit')},
        {code: "2", name: t('commonFields.typeCounted')}
    ]
    const [coins, setCoins] = useState([]);
    const [retentions, setRetentions] = useState([]);
    const [meansOfPayments, setMeansOfPayments] = useState([]);
    const [resolutions, setResolutions] = useState([]);
    
    useImperativeHandle(ref, () => ({
        getData: () => dto,
        errorInputs,
        validForm: () => validForm(fieldsRequired, dto)
    }));

    useEffect(() => {
        calculateRetentions()
    }, [dto.products, dto.retentions, dto.gross_total_value, dto.ica_retention_rate]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [menuInvoiceData, resolutionsData] = await Promise.all([
                    fetchServiceJson("MENU-INVOICE", userSession.token),
                    fecthResolutions(supplierId, userSession.token)
                ]);
                if (menuInvoiceData?.data) {
                    menuInvoiceData.data.forEach((item) => {
                        try {
                            const values = JSON.parse(item.value);
                            if (item.field === "retentions") {
                                setRetentions(values); 
                            } else if (item.field === "coins") {
                                setCoins(values);
                            } else if (item.field === "means_of_payment") {
                                setMeansOfPayments(values);
                            }
                        } catch (jsonError) {
                            console.error("Error parsing JSON for item:", item, jsonError);
                        }
                    });
                }
                if (resolutionsData?.data) {
                    setResolutions(resolutionsData?.data)
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
    }, [supplierId, userSession.token]);

    const handleChangeDate = (e, name) => handleDirectInputChange(name, moment(e).format('YYYY/MM/DD'));

    const handleSelectRetentions = (event) => {
        const { target: { value } } = event;
        handleDirectInputChange('retentions', typeof value === 'string' ? value.split(',') : value);
        if(dto.products > 0)
            calculateRetentions()
    };

    const calculateRetentions = () => {
        const subTotal = parseFloat(dto.gross_total_value || 0);
            const percentIca = parseFloat(dto.ica_retention_rate || 0);
            const iva = calculateIvaProduct(dto.products);
            let retefuente = 0;
            let reteiva = 0;
            let reteica = 0;
            dto.retentions.forEach((retentionValue) => {
                const retention = retentions.find(r => r.value === retentionValue);
                if (!retention) return;
                const key = retention.key;
                if (key.includes('ReteFuente')) {
                    const match = key.match(/([\d.]+)%/);
                    const percent = match ? parseFloat(match[1]) : 0;
                    retefuente += (subTotal * percent) / 100;
                }
                if (key.includes('ReteIVA')) {
                    const match = key.match(/([\d.]+)%/);
                    const percent = match ? parseFloat(match[1]) : 0;
                    reteiva += (iva * percent) / 100;
                }
                if (key.includes('ReteICA')) {
                    reteica += (subTotal * percentIca) / 1000;
                }
            });
            const totalWithholdings = retefuente + reteiva + reteica;
            handleDirectInputChange('total_retentions', totalWithholdings.toFixed(2));
            setRefreshTotals(true)
    }
    const calculateIvaProduct = (products) => {
        let ivaTotal = 0;
        products.forEach((product) => {
            if (product.tax && product.tax.toUpperCase().includes('IVA')) {
                ivaTotal += product.total;
            }
        });
        return ivaTotal;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        handleDirectInputChange(name, value);
        if (name === 'id_mean_of_payment') {
            const selectedPayment = meansOfPayments.find(payment => payment.code === value);
            if (selectedPayment) {
                handleDirectInputChange('mean_of_payment_text', selectedPayment.name);
            }
        }
    };
    return (
        <Box sx={{ mb: 2 }}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth disabled size="small" label={t('commonFields.documentId')} name="document_id"
                        value={
                            dto?.document_id &&
                            (dto.document_id.toLowerCase().includes('assigned') ||dto.document_id.toLowerCase().includes('asignado'))
                            ? t('commonFields.defaultDocumentId')
                            : dto?.document_id || t('commonFields.defaultDocumentId')
                        }
                        onChange={handleInputChange} 
                    />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker name="expedition_date" label={t('commonFields.expeditionDate')}
                            maxDate={new Date()}
                            value={dto.expedition_date}
                            onChange={(e) => handleChangeDate(e, 'expedition_date')}
                            renderInput={(params) => (
                                <TextField size="small" fullWidth InputLabelProps={{ shrink: true }} {...params} />
                            )}
                            inputFormat="dd/MM/yyyy"
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small" error={Boolean(errorInputs.resolution?.isError)}>
                        <InputLabel>{t('commonFields.resolutionNumber')}</InputLabel>
                        <Select name="resolution"
                            value={dto.resolution || ''}
                            onChange={handleInputChange}
                        >
                            {resolutions.map((resoltion, i) => <MenuItem key={i} value={resoltion.code}>{resoltion.name}</MenuItem>)}
                        </Select>
                        {errorInputs.resolution?.isError && (<FormHelperText>{errorInputs.resolution.messageError}</FormHelperText>)}
                    </FormControl>
                </Grid>
                
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.currency?.isError)}
                    >
                        <InputLabel>{t('commonFields.currency')}</InputLabel>
                        <Select label={t('commonFields.currency')} name="currency"
                            value={dto.currency}
                            onChange={handleInputChange}
                        >
                            {coins.map((coin, i) => <MenuItem key={i} value={coin.code}>{coin.name}</MenuItem>)}
                        </Select>
                        {errorInputs.currency?.isError && (<FormHelperText>{errorInputs.currency.messageError}</FormHelperText>)}
                    </FormControl>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small"
                        error={Boolean(errorInputs.id_payment_method?.isError)}
                    >
                        <InputLabel>{t('commonFields.paymentMethods')}</InputLabel>
                        <Select label={t('commonFields.paymentMethods')} name="id_payment_method"
                            value={dto.id_payment_method}
                            onChange={handleInputChange}
                            MenuProps={{ PaperProps: { style: { maxHeight: 200, overflowY: "auto" }}}}
                        >
                            {paymentMethods.map((item) => <MenuItem key={item.code} value={item.code}>{item.name}</MenuItem>)}
                        </Select>
                        {errorInputs.id_payment_method?.isError && <FormHelperText>{errorInputs.id_payment_method.messageError}</FormHelperText>}
                    </FormControl>
                </Grid>
                
                {dto.id_payment_method === '1' && (
                    <Grid size={{ xs: 12, md: 4 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker name="payment_due_date" label={t('commonFields.expirationDate')}
                                minDate={new Date()}
                                value={dto.payment_due_date}
                                onChange={(e) => handleChangeDate(e, 'payment_due_date')}
                                renderInput={(params) => (
                                    <TextField size="small" fullWidth InputLabelProps={{ shrink: true }} {...params} />
                                )}
                                inputFormat="dd/MM/yyyy"
                            />
                        </LocalizationProvider>
                    </Grid>
                )}

                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth required size="small" error={Boolean(errorInputs.id_mean_of_payment?.isError)}>
                        <InputLabel>{t('commonFields.meanOfPayment')}</InputLabel>
                        <Select
                            name="id_mean_of_payment"
                            value={dto.id_mean_of_payment || ''}
                            onChange={handleChange}
                            MenuProps={{PaperProps: {className: 'select-dropdown-scroll'}}}
                        >   
                            {meansOfPayments.map((meansOfPayment, i) => <MenuItem key={i} value={meansOfPayment.code}>{meansOfPayment.name}</MenuItem>)}
                        </Select>
                        {errorInputs.id_mean_of_payment?.isError && (<FormHelperText>{errorInputs.id_mean_of_payment.messageError}</FormHelperText>)}
                    </FormControl>
                </Grid> 

                <Grid size={{ xs: 12, md: 4 }}>
                    <MultipleSelect
                        data={retentions}
                        values={typeof dto.retentions === "string" ? dto.retentions === '' ? [] : dto.retentions.split(',') : dto.retentions}
                        handleChange={handleSelectRetentions}
                        label={t('commonFields.retentions')}
                    />
                </Grid> 

                {dto.retentions.includes("01") && (
                    <>
                        <Grid size={{ xs:12, md:2.5 }}>
                            <TextField fullWidth required type="number" size="small" name="ica_retention_rate"  label={t('commonFields.icaRetentionRate')} className='input-no-spinner'
                                value={dto.ica_retention_rate || ''}
                                onChange={handleInputChange}
                                onBlur={calculateRetentions}
                                error={Boolean(errorInputs.ica_retention_rate?.isError)}
                                helperText={errorInputs.ica_retention_rate?.messageError}
                                InputProps={{ endAdornment: <span style={{ marginLeft: 4 }}>‰</span> }}
                            />
                        </Grid>
                        <Grid size={{ xs:12, md:1.5 }}>
                            <TextField fullWidth disabled size="small" value={t('commonFields.perThousand')} />
                        </Grid>
                    </>
                )}

                <Grid size={{ xs: 12, md: 4 }}>
                    <TextField fullWidth size="small" label={t('commonFields.orderReference')} name="order_reference" value={dto?.order_reference} onChange={handleInputChange} />
                </Grid>

                {dto?.order_reference !== '' && (
                    <Grid size={{ xs: 12, md: 4 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker name="order_reference_date" label={t('commonFields.orderReferenceDate')}
                                maxDate={new Date()}
                                value={dto.order_reference_date}
                                onChange={(e) => handleChangeDate(e, 'order_reference_date')}
                                renderInput={(params) => <TextField size="small" fullWidth InputLabelProps={{ shrink: true }} {...params} />}
                                inputFormat="dd/MM/yyyy"
                            />
                        </LocalizationProvider>
                    </Grid>
                )}
                
                {/* <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>{t('commonFields.foreignCurrency')}</InputLabel>
                        <Select name="foreign_currency_value"
                            value={dto.foreign_currency_value || 'NO'}
                            onChange={handleInputChange}
                        >
                            <MenuItem value="NO">{t('no')}</MenuItem>
                            <MenuItem value="YES">{t('yes')}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid> */}
            </Grid>
        </Box>
    );
});
